import {
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import Layout from '../../../layout/Layout'
import { useSidebar } from '../../../../contexts/SidebarContext'
import { fieldOptions } from '../../Define/configs/ruleSettingTableConfig'
import { z } from 'zod'
import CustomInput from '../../CustomInput'
import CustomSelect from '../../CustomSelect'

/* ------------ Zod Validation Schema ------------ */
const duplicationRuleSchema = z.object({
  ruleName: z.string().trim().min(1, 'Rule name is required'),
  ruleStatus: z.literal('ACTIVE'),
  parameters: z
    .array(z.string().min(1, 'Parameter is required'))
    .min(1, 'At least one parameter is required')
    .max(8, 'You can add maximum 8 parameters')
    .superRefine((params, ctx) => {
      const seen = new Set()
      params.forEach((value, index) => {
        if (seen.has(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'This parameter is already selected. Please choose a different parameter',
            path: [index],
          })
        }
        seen.add(value)
      })
    }),
  uuid: z.string().optional(),
})

/* ------------ Helper: Compute Duplicate Errors ------------ */
const getDuplicateErrors = params => {
  const seen = new Set()
  const errors = {}

  params.forEach((value, index) => {
    if (!value) return
    if (seen.has(value)) {
      errors[index] = 'This parameter is already selected. Please choose a different parameter'
    } else {
      seen.add(value)
    }
  })

  return errors
}

const AddDuplicationRule = ({
  onCancel,
  onSave,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  isEdit,
}) => {
  const { isCollapsed } = useSidebar()

  /* ------------ Add Parameter ------------ */
  const handleAddParameter = () => {
    if (formData.parameters.length >= 8) return
    setFormData(prev => ({
      ...prev,
      parameters: [...prev.parameters, ''],
    }))
  }

  /* ------------ Delete Parameter ------------ */
  const handleDeleteParameter = index => {
    const updated = formData.parameters.filter((_, i) => i !== index)
    setFormData(prev => ({ ...prev, parameters: updated }))
    setFormErrors(getDuplicateErrors(updated))
  }

  /* ------------ Update Parameter ------------ */
  const handleParameterChange = (index, value) => {
    const updated = formData.parameters.map((p, i) => (i === index ? value : p))

    setFormData(prev => ({ ...prev, parameters: updated }))
    setFormErrors(getDuplicateErrors(updated))
  }

  /* ------------ Rule Name ------------ */
  const handleRuleNameChange = e => {
    const value = e.target.value
    setFormData(prev => ({ ...prev, ruleName: value }))

    if (formErrors.ruleName) {
      setFormErrors(prev => ({ ...prev, ruleName: '' }))
    }
  }

  /* ------------ Save ------------ */
  const handleSave = () => {
    const result = duplicationRuleSchema.safeParse({
      ruleName: formData.ruleName,
      ruleStatus: 'ACTIVE',
      parameters: formData.parameters,
      ...(isEdit ? { uuid: formData?.uuid } : {}),
    })

    if (!result.success) {
      const newErrors = {}

      result.error.issues.forEach(err => {
        if (err.path[0] === 'parameters') {
          const index = err.path[1]
          newErrors[index] = err.message
        } else {
          newErrors[err.path[0]] = err.message
        }
      })

      setFormErrors(newErrors)
      return
    }

    setFormErrors({})
    onSave(result.data)
  }

  return (
    <Layout>
      <Box sx={{ p: 3, pb: 10 }}>
        <Typography variant="h6">Add Parameters</Typography>
        <Typography variant="caption" color="error">
          (*) Fields are mandatory
        </Typography>

        {/* Rule Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 3 }}>
          <CustomInput
            label="Rule Name"
            required
            placeholder="Enter rule name"
            value={formData.ruleName}
            onChange={handleRuleNameChange}
            error={!!formErrors.ruleName}
            helperText={formErrors.ruleName}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              mt: 3,
              border: '1px solid var(--border-subtel)',
            }}
          >
            <Typography color="var(--forbrand-color)" fontWeight={600}>
              Active
            </Typography>
          </Box>
        </Box>

        {/* Parameters */}
        {formData.parameters.map((param, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 2,
              mt: 3,
              alignItems: 'flex-start',
              width: '70vw',
            }}
          >
            <CustomSelect
              label={`Parameter ${index + 1}`}
              required
              placeholder="Select parameter"
              value={param}
              onChange={e => handleParameterChange(index, e.target.value)}
              options={fieldOptions.filter((items) => items.value !== "DUPLICATE_REFERENCE_NUMBER")}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              error={!!formErrors[index]}
              helperText={formErrors[index]}
              fullWidth
            />

            {formData.parameters.length > 1 && (
              <IconButton
                onClick={() => handleDeleteParameter(index)}
                sx={{
                  color: 'var(--forbrand-error)',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  py: '12px',
                  px: '12px',
                  mt: 4,
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            )}
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddParameter}
          disabled={formData.parameters.length >= 8}
          sx={{
            mt: 3,
            color: formData.parameters.length >= 8 ? '#999' : 'error.main',
            textTransform: 'none',
            opacity: formData.parameters.length >= 8 ? 0.9 : 1,
            cursor: formData.parameters.length >= 8 ? 'not-allowed' : 'pointer',
          }}
        >
          Add Parameter
        </Button>
      </Box>

      {/* Bottom Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, lg: isCollapsed ? '66px' : '300px' },
          right: 0,
          backgroundColor: '#fff',
          borderTop: '1px solid #e0e0e0',
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ color: 'var(--forbrand-primary)', border: '1px solid gray' }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              fontSize: '16px'
            }}
          >
            {formData?.uuid ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Layout>
  )
}

export default AddDuplicationRule
